/**
 * PHARMAKON — Trésorerie · M4, the receiving side of a counter transfer (TWP-22; design/ux/02 §13.4,
 * AC-M4-04, TD-129, TA-75 (10)).
 *
 * Mounted ONCE in the shell (next to the alertes host) for the counter roles that hear only their
 * comptoir's frames (a role that may operate a till — `tresorerie:create` — without the officine's
 * treasury audience — `tresorerie_pharmacie:read`; the server's `ROLES_TEMPS_REEL_COMPTOIR`): wherever
 * the caissier is (the sale screen most of the time), a transfer received while he works raises an
 * Info toast — « 5 000,00 DA reçus de Comptoir 2 (M. B.) — Transfert : monnaie pour le comptoir 1 » —
 * with « Voir »; under the officine option « en transit » it reads « 5 000,00 DA en transit depuis
 * Comptoir 2 » with « Accuser réception ». The toast never takes focus (role="status", ToastHost).
 *
 * How it knows: the server sends ONE frame to the holder of the comptoir a committed write touched
 * (keys of HIS comptoir, no amount); the keyed store re-reads `GET /comptoirs/moi` on it (the entry
 * the card shares) and this host compares the reading with the previous one (`nouveautesReception`):
 * the words come from the server's rows, never from the frame. Nothing is polled; nothing renders.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { describeError } from "@/components/ui/primitives";
import { useAuthStore } from "@/stores/authStore.js";
import { can } from "@/config/permissions.config.js";
import { fmtMoney } from "@/config/locale.config.js";
import { RECEPTION as M } from "@/config/tresorerie-caissier.messages.js";
import { estRoleComptoirSeulement, nouveautesReception, routeOperationMaCaisse } from "@/lib/tresorerie/caissier.js";
import { useMonComptoir } from "@/hooks/useMonComptoir.js";
import { useToast } from "@/hooks/useToast.js";
import { appliquerEcritureTresorerie } from "@/hooks/useTresorerieQuery.js";
import { tresoreriePharmacieApi } from "@/services/index.js";

export function ReceptionComptoir() {
  const user = useAuthStore((s) => s.user);
  const actif = estRoleComptoirSeulement(user, can);
  return actif ? <EcouteReception userId={user.id} /> : null;
}

function EcouteReception({ userId }) {
  const navigate = useNavigate();
  const toast = useToast();
  const lu = useMonComptoir({ actif: true });
  const precedent = React.useRef(null);

  React.useEffect(() => {
    const apres = lu.data;
    if (!apres) return;
    const avant = precedent.current;
    precedent.current = apres;
    for (const n of nouveautesReception(avant, apres, { moi: userId })) {
      const l = n.ligne;
      if (n.genre === "recu") {
        toast.info(M.recu(fmtMoney(l.montant), l.depuis?.libelle ?? "", l.saisi_par?.nom ?? null, l.motif, l.note), undefined, {
          action: { label: M.voir, onClick: () => navigate(routeOperationMaCaisse(l.id)) },
        });
      } else {
        toast.info(M.transit(fmtMoney(l.montant), l.depuis?.libelle ?? ""), undefined, {
          action: {
            label: M.accuser,
            onClick: () =>
              tresoreriePharmacieApi
                .accuserReception(l.id)
                .then((r) => appliquerEcritureTresorerie(r?.data ?? r ?? {}))
                .catch((e) => toast.error(describeError(e).message)),
          },
        });
      }
    }
  }, [lu.data, userId, navigate, toast]);

  return null;
}

export default ReceptionComptoir;
